import { useMemo } from 'react';
import type { FC } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { getFourGiftCardMessagesAsOneString } from '../../../../bff-api/utils/basket-utils';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { useIsMobile } from '../../../../hooks/use-is-mobile/useIsMobile';
import { Text } from '../../../../ui-components';
import { isElectronicGiftCard } from '../../../../utils/product-utils';
import { ProductAvailabilityMessages } from '../cart-quantity-box/ProductAvailabilityMessages';
import { BaseItem } from './BaseItem';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';
import type { Product } from '../../../../bff-api/products/model';
import type { BaseItemProps } from './BaseItem';

export type GiftCardItemProps = {
    productItem: ProductItemEditor;
    product?: Product;
    variation?: string;
} & Omit<BaseItemProps, 'id'>;

export const GiftCardItem: FC<GiftCardItemProps> = ({ variation, ...props }) => {
    const { productItem } = props;
    const isEGC = useMemo(() => isElectronicGiftCard(productItem), [productItem]);
    const personalMessage = useMemo(
        () => getFourGiftCardMessagesAsOneString(productItem),
        [productItem],
    );

    const isMobile = useIsMobile();

    return (
        <BaseItem
            {...props}
            id={`cart-item-${isEGC ? 'e' : 'p'}-gift-card-${productItem.productId}`}
            quantitySection={<ProductAvailabilityMessages productItem={productItem} />}
            middleSection={
                <Flex direction="column">{variation ? <Text>{variation}</Text> : null}</Flex>
            }
            footerSection={
                isEGC || !!personalMessage ? (
                    <Flex
                        gap={6}
                        direction="column"
                        width="100%"
                        data-testid="gift-card-item-footer-block"
                    >
                        <Flex gap={1} direction="column">
                            <ConditionalRender visibility={isEGC}>
                                <Flex gap={1}>
                                    <Text style="bold">
                                        <FormattedMessage
                                            id="cart-item.giftcard.to"
                                            defaultMessage="To:"
                                        />
                                    </Text>
                                    <Text>
                                        <FormattedMessage
                                            id="cart-item.giftcard.to.text"
                                            defaultMessage="{recipientEmail}"
                                            values={{
                                                recipientEmail: productItem?.c_gcRecipientEmail,
                                            }}
                                        />
                                    </Text>
                                </Flex>
                            </ConditionalRender>
                            <ConditionalRender visibility={!!personalMessage}>
                                <Flex
                                    direction="column"
                                    gap={1}
                                    maxWidth={isMobile ? '100%' : '80%'}
                                >
                                    <Text style="bold" margin="0 0.25rem 0 0">
                                        <FormattedMessage
                                            id="cart-item.giftcard.message"
                                            defaultMessage="Message:"
                                        />
                                        <Text style="regular" wordBreak="break-word">
                                            {' '}
                                            {personalMessage}
                                        </Text>
                                    </Text>
                                </Flex>
                            </ConditionalRender>
                        </Flex>

                        <ConditionalRender visibility={isEGC}>
                            <Flex
                                padding="0.25rem 0.5rem"
                                background="slt-grey.150"
                                border="1px solid #C4BFC0"
                                borderRadius={4}
                                width="100%"
                                sx={{
                                    ':empty': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <Text>
                                    <FormattedMessage
                                        id="cart-item.giftcard.note"
                                        defaultMessage="Note: Gift certificate will be delivered by email."
                                    />
                                </Text>
                            </Flex>
                        </ConditionalRender>
                    </Flex>
                ) : null
            }
        />
    );
};

GiftCardItem.defaultProps = {
    product: undefined,
    variation: undefined,
};
